import React, { useState, useEffect, useRef } from "react";
import "../../Components/Table/DynamicTable.css";

const DynamicTable = ({
  columns = [],
  rows = [],
  height = 400,
  emptyComponent = null,
  onRowClick = null,
  searchable = true,
  getRowClassName = null,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const containerRef = useRef(null);

  const getSortValue = (row, col) => {
    if (col.sortValue && typeof col.sortValue === "function") {
      return col.sortValue(row);
    }

    // Fallback to row[col.field] if it exists
    if (row[col.field] !== undefined) {
      return row[col.field];
    }

    // Attendance-specific nested mapping fallbacks
    const field = col.field;
    if (field === "employeeName" || field === "employee") {
      return row.name;
    }
    if (row.monthlyStats) {
      if (field === "present") return row.monthlyStats.presentDays;
      if (field === "absent") return row.monthlyStats.absentDays;
      if (field === "leave") return row.monthlyStats.leaveDays;
      if (field === "halfDay") return row.monthlyStats.halfDays;
      if (field === "lates") return row.monthlyStats.lateCount;
      if (field === "lateDuration") return row.monthlyStats.lateMinutes;
      if (field === "earlies") return row.monthlyStats.earlyCount;
      if (field === "earlyDuration") return row.monthlyStats.earlyOutMinutes;
      if (field === "totalDeduction") {
        return (row.monthlyStats.lateMinutes || 0) + (row.monthlyStats.earlyOutMinutes || 0);
      }
      if (field === "hoursWorked") return row.monthlyStats.workingMinutes;
    }
    if (field === "deptRole") {
      return row.department;
    }

    return null;
  };

  const handleSort = (field, sortable) => {
    if (sortable === false) return;
    let direction = "ascending";
    if (sortConfig.key === field && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (sortConfig.key === field && sortConfig.direction === "descending") {
      direction = null;
    }
    setSortConfig({ key: direction ? field : null, direction });
  };

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth(); // Initial call
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const filteredRows =
    rows?.filter((row) =>
      Object.values(row).some(
        (val) =>
          val &&
          val.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    ) || [];

  const sortedRows = React.useMemo(() => {
    let sortableItems = [...filteredRows];
    if (sortConfig.key !== null) {
      const activeCol = columns.find((c) => c.field === sortConfig.key);
      if (activeCol) {
        sortableItems.sort((a, b) => {
          let aVal = getSortValue(a, activeCol);
          let bVal = getSortValue(b, activeCol);

          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;

          // If both values are strings, compare them alphabetically (case-insensitive)
          if (typeof aVal === "string" && typeof bVal === "string") {
            const cleanA = aVal.trim().toLowerCase();
            const cleanB = bVal.trim().toLowerCase();

            // Check if they look like durations "4h 50m" or "171 hrs"
            const parseDuration = (val) => {
              let minutes = 0;
              const hMatch = val.match(/(\d+)\s*(?:h|hr|hrs)/);
              const mMatch = val.match(/(\d+)\s*(?:m|min|mins)/);
              if (hMatch) minutes += parseInt(hMatch[1]) * 60;
              if (mMatch) minutes += parseInt(mMatch[1]);
              if (minutes > 0) return minutes;

              const floatVal = parseFloat(val);
              if (!isNaN(floatVal)) return floatVal;
              return null;
            };

            const durA = parseDuration(cleanA);
            const durB = parseDuration(cleanB);

            if (durA !== null && durB !== null) {
              return sortConfig.direction === "ascending" ? durA - durB : durB - durA;
            }

            return sortConfig.direction === "ascending"
              ? aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' })
              : bVal.localeCompare(aVal, undefined, { numeric: true, sensitivity: 'base' });
          }

          // Numeric comparison
          if (typeof aVal === "number" && typeof bVal === "number") {
            return sortConfig.direction === "ascending" ? aVal - bVal : bVal - aVal;
          }

          // Generic comparison
          if (aVal < bVal) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (aVal > bVal) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }
    }
    return sortableItems;
  }, [filteredRows, sortConfig, columns]);

  return (
    <div
      ref={containerRef}
      className="w-full overflow-x-auto"
      style={{ width: "100%" }}
    >
      {searchable && (
        <div className="flex justify-start mb-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="w-full md:w-1/6 border-2 border-black rounded-md px-3 py-2 text-sm  outline-none transition-all focus:border-[#133a69] focus:shadow-md"
          />
        </div>
      )}
      <div
        className="min-w-full border border-[#122A47] rounded-xl overflow-hidden relative z-0"
        style={{ width: containerWidth }}
      >
        <div
          className="overflow-y-auto scrollbar-custom"
          style={{ height: typeof height === "number" ? `${height}px` : height }}
        >
          <table className="min-w-full table-auto">
            <thead className="bg-[#133a69] text-white text-left sticky top-0 z-50">
              <tr>
                {columns.map((col, index) => {
                  const isSortable = col.sortable !== false;
                  return (
                    <th
                      key={col.field || index}
                      className={`px-4 py-3 text-sm whitespace-nowrap font-semibold ${isSortable ? "cursor-pointer select-none hover:bg-[#1a4a82]" : ""
                        }`}
                      onClick={() => isSortable && handleSort(col.field, isSortable)}
                      style={{
                        minWidth: col.flex ? `${col.flex * 100}px` : "150px",
                        position: col.sticky ? "sticky" : "static",
                        left: col.sticky ? 0 : "auto",
                        zIndex: col.sticky ? 40 : 20,
                        background: "#133a69",
                        boxShadow: col.sticky ? "2px 0 5px rgba(0,0,0,0.1)" : "none",
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{col.headerName}</span>
                        {isSortable && (
                          <span
                            className="text-[10px] text-blue-200"
                            style={{ opacity: sortConfig.key === col.field ? 1 : 0.4 }}
                          >
                            {sortConfig.key === col.field ? (
                              sortConfig.direction === "ascending" ? " ▲" : " ▼"
                            ) : (
                              " ↕"
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-[#F3F7FA] text-gray-800">
              {sortedRows.length > 0 ? (
                sortedRows.map((row, rowIndex) => (
                  <tr
                    key={row.id || rowIndex}
                    className={`
                      border-b border-gray-200 last:border-b-0
                      ${onRowClick ? "cursor-pointer transition-all duration-200" : ""}
                      ${(getRowClassName ? getRowClassName(row) : null) || row.className || (rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]')}
                      ${hoveredRow === rowIndex ? '!shadow-inner' : ''}
                      hover:bg-[#F0F7FF]
                    `}
                    onClick={() => onRowClick && onRowClick(row)}
                    onMouseEnter={() => setHoveredRow(rowIndex)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      transition: "all 0.2s ease-in-out",
                      position: "relative",
                    }}
                  >
                    {columns.map((col, colIndex) => {
                      const params = {
                        row,
                        value: row[col.field],
                        field: col.field,
                      };
                      return (
                        <td
                          key={col.field || colIndex}
                          className={`
                            px-4 py-3 text-sm whitespace-nowrap
                            ${hoveredRow === rowIndex ? 'text-[#133a69] font-medium' : ''}
                          `}
                          style={{
                            minWidth: col.flex
                              ? `${col.flex * 100}px`
                              : "150px",
                            position: col.sticky ? "sticky" : "static",
                            left: col.sticky ? 0 : "auto",
                            zIndex: col.sticky ? 30 : 10,
                            background: col.sticky
                              ? hoveredRow === rowIndex
                                ? "#E8F2FF"
                                : (rowIndex % 2 === 0 ? 'white' : '#F8FAFC')
                              : "transparent",
                            boxShadow: col.sticky
                              ? hoveredRow === rowIndex
                                ? "2px 0 5px rgba(19, 58, 105, 0.2)"
                                : "2px 0 5px rgba(0,0,0,0.05)"
                              : "none",
                            borderRight: col.sticky ? "1px solid #E5E7EB" : "none",
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          {col.renderCell
                            ? col.renderCell(params)
                            : (row[col.field] ?? "--")}
                        </td>
                      );
                    })}
                    {/* Hover effect line */}
                    {hoveredRow === rowIndex && (
                      <div
                        className="absolute left-0 top-0 h-full w-1 bg-[#133a69] opacity-80"
                        style={{
                          zIndex: 35,
                        }}
                      />
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center border-none"
                    style={{ height: `${height - 55}px` }}
                  >
                    {emptyComponent || (
                      <div className="flex flex-col items-center justify-center h-full">
                        <div className="w-16 h-16 bg-[#F0F7FF] rounded-full flex items-center justify-center mb-4 shadow-sm border border-[#E8F2FF]">
                          <svg className="w-8 h-8 text-[#133a69]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                        </div>
                        <h3 className="text-base font-bold text-[#133a69] mb-1 tracking-wide">No tasks available</h3>
                        <p className="text-gray-500 text-sm text-center max-w-[280px] leading-relaxed">
                          There are currently no tasks in this section.
                        </p>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DynamicTable;